import React from 'react';

interface ChartGhostStateProps {
  /** True when there isn't enough data to plot/compare anything meaningful. */
  active: boolean;
  message: string;
  children: React.ReactNode;
}

/**
 * Wraps a chart or comparison table so that when there isn't enough data to
 * populate it, the real thing still renders underneath — its own empty
 * axes/grid/dash placeholders act as the skeleton of what will eventually
 * show — dimmed and inert, with an explanatory prompt overlaid on top.
 * Never unmounts the chart itself: the goal is "here's the shape of what
 * you'll get once there's data," not a blank space where a chart used to be.
 */
const ChartGhostState: React.FC<ChartGhostStateProps> = ({ active, message, children }) => (
  <div className="relative">
    <div
      aria-hidden={active || undefined}
      className={active ? 'pointer-events-none select-none opacity-40' : undefined}
    >
      {children}
    </div>
    {active && (
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <p className="max-w-xs rounded-md border border-dashed border-gray-argent bg-white/90 px-4 py-2 text-center text-sm font-semibold text-gray-dark shadow-sm">
          {message}
        </p>
      </div>
    )}
  </div>
);

export default ChartGhostState;
